import maplibregl from "maplibre-gl";
import type { PackRouter } from "./packRouter";
import type { PackDescriptor } from "../storage/manifest";

const DEFAULT_ZOOM = 9;
const EMPTY_MAP_ZOOM = 2;
const VIEW_STORAGE_KEY = "klint.view";
const HIDDEN_MAPS_STORAGE_KEY = "klint.hiddenMaps";
const MAP_LAYER_OPACITY = 0.88;

interface SavedView {
  center: [number, number];
  zoom: number;
  bearing: number;
}

function loadSavedView(): SavedView | null {
  try {
    const raw = localStorage.getItem(VIEW_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as SavedView) : null;
  } catch {
    return null;
  }
}

export interface MapHandle {
  map: maplibregl.Map;
  geolocate: maplibregl.GeolocateControl;
  layers: MapLayerHandle[];
  setLayerVisibility: (packId: string, visible: boolean) => void;
  showSearchResults: (points: MapSearchPoint[]) => void;
  highlightSearchResult: (key: string) => void;
}

export interface MapSearchPoint {
  key: string;
  label: string;
  longitude: number;
  latitude: number;
}

export interface MapLayerHandle {
  pack: PackDescriptor;
  visible: boolean;
}

interface LayeredStyle {
  style: maplibregl.StyleSpecification;
  layerIds: Map<string, Array<{ id: string; visibility: unknown }>>;
}

function hiddenMaps(): Set<string> {
  try {
    const value = JSON.parse(localStorage.getItem(HIDDEN_MAPS_STORAGE_KEY) ?? "[]") as unknown;
    return new Set(
      Array.isArray(value)
        ? value.filter((id): id is string => typeof id === "string")
        : [],
    );
  } catch {
    return new Set();
  }
}

function saveLayerVisibility(packId: string, visible: boolean): void {
  try {
    const hidden = hiddenMaps();
    if (visible) hidden.delete(packId);
    else hidden.add(packId);
    localStorage.setItem(HIDDEN_MAPS_STORAGE_KEY, JSON.stringify([...hidden]));
  } catch {
    // Visibility still changes for this session if storage is unavailable.
  }
}

function routedSource(source: unknown): string | null {
  if (!source || typeof source !== "object") return null;
  const record = source as Record<string, unknown>;
  if (record.type !== "vector" || typeof record.url !== "string") return null;
  return /^pmtiles:\/\/([^/]+)$/.exec(record.url)?.[1] ?? null;
}

function opacityProperties(type: string): string[] {
  switch (type) {
    case "fill":
      return ["fill-opacity"];
    case "line":
      return ["line-opacity"];
    case "circle":
      return ["circle-opacity", "circle-stroke-opacity"];
    case "symbol":
      return ["icon-opacity", "text-opacity"];
    case "raster":
      return ["raster-opacity"];
    case "heatmap":
      return ["heatmap-opacity"];
    case "fill-extrusion":
      return ["fill-extrusion-opacity"];
    default:
      return [];
  }
}

function applyMapOpacity(layer: Record<string, unknown>): void {
  const type = typeof layer.type === "string" ? layer.type : "";
  const paint = (layer.paint && typeof layer.paint === "object"
    ? layer.paint
    : {}) as Record<string, unknown>;
  for (const property of opacityProperties(type)) {
    const existing = paint[property];
    paint[property] =
      existing === undefined
        ? MAP_LAYER_OPACITY
        : typeof existing === "number"
          ? existing * MAP_LAYER_OPACITY
          : existing;
  }
  layer.paint = paint;
}

/**
 * The generated style describes kinds of data (OSM and S-57). At runtime each
 * installed archive becomes its own source and receives a copy of the matching
 * style layers. This lets overlapping maps render together and be toggled
 * independently instead of the tile router silently choosing one archive.
 */
function makeLayeredStyle(
  sourceStyle: maplibregl.StyleSpecification,
  packs: PackDescriptor[],
): LayeredStyle {
  const style = structuredClone(sourceStyle) as maplibregl.StyleSpecification;
  const sourceRoutes = new Map<string, string>();
  const retainedSources: Record<string, unknown> = {};
  for (const [sourceId, source] of Object.entries(style.sources ?? {})) {
    const route = routedSource(source);
    if (route) sourceRoutes.set(sourceId, route);
    else retainedSources[sourceId] = source;
  }

  const packsByRoute = new Map<string, PackDescriptor[]>();
  for (const pack of packs) {
    const list = packsByRoute.get(pack.source) ?? [];
    list.push(pack);
    packsByRoute.set(pack.source, list);
    retainedSources[`klint:${pack.id}`] = {
      type: "vector",
      url: `pmtiles://pack/${encodeURIComponent(pack.id)}`,
    };
  }

  const hidden = hiddenMaps();
  const layerIds = new Map<string, Array<{ id: string; visibility: unknown }>>();
  const layers: unknown[] = [];
  for (const original of style.layers ?? []) {
    const layer = original as unknown as Record<string, unknown>;
    const sourceId = typeof layer.source === "string" ? layer.source : null;
    const route = sourceId ? sourceRoutes.get(sourceId) : null;
    if (!route) {
      layers.push(original);
      continue;
    }

    for (const pack of packsByRoute.get(route) ?? []) {
      const clone = structuredClone(layer);
      clone.id = `${String(layer.id)}::klint:${pack.id}`;
      clone.source = `klint:${pack.id}`;
      applyMapOpacity(clone);
      const layout = (clone.layout && typeof clone.layout === "object"
        ? clone.layout
        : {}) as Record<string, unknown>;
      const originalVisibility = layout.visibility;
      if (hidden.has(pack.id)) layout.visibility = "none";
      clone.layout = layout;
      const ids = layerIds.get(pack.id) ?? [];
      ids.push({ id: String(clone.id), visibility: originalVisibility });
      layerIds.set(pack.id, ids);
      layers.push(clone);
    }
  }

  style.sources = retainedSources as maplibregl.StyleSpecification["sources"];
  style.layers = layers as maplibregl.StyleSpecification["layers"];
  return { style, layerIds };
}

/**
 * MapLibre insists on an absolute sprite URL, but the deployment origin is not
 * known when the style is generated, so the relative path is resolved against
 * the running page here. The style itself is precached, so this still works
 * with no network.
 */
async function loadStyle(styleUrl: string): Promise<maplibregl.StyleSpecification> {
  const response = await fetch(styleUrl);
  if (!response.ok) throw new Error(`Could not load the map style (${response.status}).`);
  const style = (await response.json()) as maplibregl.StyleSpecification;
  // Only the sprite: the glyphs URL carries {fontstack} and {range} tokens that
  // URL() would percent-encode, and MapLibre resolves that one relatively.
  if (typeof style.sprite === "string") {
    style.sprite = new URL(style.sprite, document.baseURI).toString();
  }
  return style;
}

export async function createMap(
  container: HTMLElement,
  styleUrl: string,
  router: PackRouter,
  /** Where to look when there is no saved view - the maps the user actually has. */
  fallbackCenter: [number, number],
): Promise<MapHandle> {
  router.register();

  const saved = loadSavedView();
  const packs = router.packs();
  const layered = makeLayeredStyle(await loadStyle(styleUrl), packs);
  const map = new maplibregl.Map({
    container,
    style: layered.style,
    center: saved?.center ?? fallbackCenter,
    zoom: saved?.zoom ?? (packs.length ? DEFAULT_ZOOM : EMPTY_MAP_ZOOM),
    bearing: saved?.bearing ?? 0,
    // The maps stop at z14; MapLibre overzooms those tiles so the crew can
    // still magnify a harbour approach.
    maxZoom: 18,
    minZoom: 2,
    attributionControl: false,
    renderWorldCopies: false,
    // Keeps WebKit's per-tab memory ceiling out of reach on older iPhones.
    maxTileCacheSize: 128,
    fadeDuration: 0,
  });

  if (import.meta.env.DEV) {
    (window as unknown as { __klintMap?: maplibregl.Map }).__klintMap = map;
  }

  map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
  map.addControl(
    new maplibregl.ScaleControl({ maxWidth: 120, unit: "nautical" }),
    "bottom-left",
  );

  const geolocate = new maplibregl.GeolocateControl({
    positionOptions: { enableHighAccuracy: true, timeout: 10_000, maximumAge: 2_000 },
    trackUserLocation: true,
    showUserLocation: true,
    showAccuracyCircle: true,
    // Without a cap the control zooms to the accuracy of the fix, well past the
    // z14 the charts actually hold, leaving the crew staring at one magnified
    // depth area.
    fitBoundsOptions: { maxZoom: 14 },
  });
  map.addControl(geolocate, "top-right");
  // Keep north reset directly beneath position, where it remains available
  // without mixing the small compass into the zoom-button group.
  map.addControl(
    new maplibregl.NavigationControl({
      showZoom: false,
      showCompass: true,
      visualizePitch: false,
    }),
    "top-right",
  );

  map.on("moveend", () => {
    const center = map.getCenter();
    const view: SavedView = {
      center: [center.lng, center.lat],
      zoom: map.getZoom(),
      bearing: map.getBearing(),
    };
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify(view));
    } catch {
      // A full or partitioned storage bucket must not break panning.
    }
  });

  // MapLibre emits `error` for recoverable things too - a font range that is
  // not in the glyph set, a tile that is briefly unavailable. Treating those as
  // fatal would tear the map down mid-passage, so they are only logged; the
  // load promise is settled by `load` or by giving up after a timeout.
  map.on("error", (event) => {
    console.warn("map:", event.error?.message ?? event.type);
  });

  await new Promise<void>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error("The map did not finish loading.")),
      30_000,
    );
    map.once("load", () => {
      clearTimeout(timer);
      resolve();
    });
  });

  const layers = packs.map((pack) => ({ pack, visible: !hiddenMaps().has(pack.id) }));
  const setLayerVisibility = (packId: string, visible: boolean) => {
    const layer = layers.find((candidate) => candidate.pack.id === packId);
    if (layer) layer.visible = visible;
    for (const item of layered.layerIds.get(packId) ?? []) {
      map.setLayoutProperty(
        item.id,
        "visibility",
        visible ? (item.visibility ?? "visible") : "none",
      );
    }
    saveLayerVisibility(packId, visible);
  };

  const searchMarkers = new Map<string, { marker: maplibregl.Marker; element: HTMLElement }>();
  const highlightSearchResult = (key: string) => {
    for (const [candidate, { element }] of searchMarkers) {
      element.classList.toggle("is-active", candidate === key);
    }
  };
  const showSearchResults = (points: MapSearchPoint[]) => {
    for (const { marker } of searchMarkers.values()) marker.remove();
    searchMarkers.clear();
    for (const point of points) {
      const element = document.createElement("div");
      element.className = "search-map-marker";
      element.dataset.longitude = String(point.longitude);
      element.dataset.latitude = String(point.latitude);
      element.setAttribute("role", "img");
      element.setAttribute("aria-label", point.label);
      const pin = document.createElement("div");
      pin.className = "search-map-marker-pin";
      pin.dataset.label = point.label;
      element.append(pin);
      const marker = new maplibregl.Marker({ element, anchor: "center" })
        .setLngLat([point.longitude, point.latitude])
        .addTo(map);
      searchMarkers.set(point.key, { marker, element });
    }
    if (!points.length) return;

    const leftPadding = Math.min(420, Math.round(map.getContainer().clientWidth * 0.45));
    const longitudes = points.map((point) => point.longitude);
    const latitudes = points.map((point) => point.latitude);
    const spread =
      Math.max(...longitudes) - Math.min(...longitudes) +
      Math.max(...latitudes) - Math.min(...latitudes);
    if (points.length === 1 || spread < 0.000_01) {
      highlightSearchResult(points[0].key);
      map.easeTo({
        center: [points[0].longitude, points[0].latitude],
        zoom: Math.max(map.getZoom(), 13),
        offset: [leftPadding / 2, 0],
        duration: 500,
      });
      return;
    }

    const bounds = new maplibregl.LngLatBounds();
    for (const point of points) bounds.extend([point.longitude, point.latitude]);
    map.fitBounds(bounds, {
      padding: { top: 60, right: 50, bottom: 60, left: leftPadding },
      maxZoom: 13,
      duration: 650,
    });
  };

  return {
    map,
    geolocate,
    layers,
    setLayerVisibility,
    showSearchResults,
    highlightSearchResult,
  };
}
